/* eslint-disable @typescript-eslint/ban-ts-ignore */
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { VueConstructor } from "vue";
import Router, { RawLocation, Route } from "vue-router";
import { Store } from "vuex";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { ACTION_TYPES, MUTATION_TYPES } from "@/store/const";
import { StateRoot } from "@/store/state";
import { getLoginData } from "@/lib/authHelper";
import HistoryManager from "@/lib/historyManager";
import { allowIdentityAccess } from "@/lib/identityHelpers";
import { allowAccess as allowAccessHelper } from "@/lib/loginHelpers";
import { allowParentalAccess, isUserUnderAge } from "@/lib/parentalConsentHelpers";
import { setReferral } from "@/lib/referralHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";

export default {
  install(Vue: VueConstructor, { router, store }: { router: Router; store: Store<StateRoot> }) {
    NProgress.configure({ showSpinner: false });
    const origReplace = router.replace;
    // @ts-ignore
    router.replace = function(...args) {
      HistoryManager.setReplace();
      // @ts-ignore
      return origReplace.call(this, ...args);
    };
    router.beforeEach(async (to, from, next) => {
      NProgress.start();
      if (to.query.redirectPath) {
        store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, to.query.redirectPath);
      }
      if (to.query.referral) {
        let referral = to.query.referral;
        const referralId = Array.isArray(referral)
          ? referral.find<string>((i): i is string => typeof i === "string") || ""
          : referral;
        setReferral(referralId);
        store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, getProfileUrl(referralId));
      }
      let location: RawLocation | false | void;
      const loginPath = (route: Route) => !!route.meta?.login || route.name === "logout" || route.path === "/";
      if (!store.state.user && getLoginData()) {
        await store.dispatch(ACTION_TYPES.LOGIN, getLoginData());
      }
      const isStaticPage = (route: Route) => route.path.includes("/static/");
      const allowAccess = allowAccessHelper(store.state.user);
      const isAllowIdentityAccess = allowIdentityAccess(store.state.identityStatus);
      const isAllowParentalAccess = allowParentalAccess(store.state.parentalConsent);
      const isUnderAge = isUserUnderAge(store.state.user?.personalInfo ?? null);
      const socialLogin = store.state.socialLogin;
      const loggedIn = !!store.state.user;
      const waitingForApproval = store.state.verified === UserVerificationStatus.WAITING_FOR_APPROVAL;
      const redirectPath = store.state.redirectPath;
      if (to.name === "loginToken") {
        next();
        return;
      } else if (to.matched.some(route => route.meta.private && !loggedIn)) {
        location = { name: "login" };
      } else if (to.name === "logout") {
        await store.dispatch(ACTION_TYPES.LOGOUT);
        location = { name: "login" };
      } else if (
        loginPath(to) &&
        loggedIn &&
        allowAccess &&
        isAllowIdentityAccess &&
        (!isUnderAge || isAllowParentalAccess)
      ) {
        location = { name: "home" };
      } else if (to.name !== "signupEmailConfirm" && loggedIn && !allowAccess && socialLogin) {
        location = { name: "signupEmailConfirm" };
      } else if (to.name !== "signupConfirm" && loggedIn && !allowAccess && !socialLogin) {
        location = { name: "signupConfirm" };
      } else if (
        to.name !== "identityVerification" &&
        loggedIn &&
        allowAccess &&
        !isAllowIdentityAccess &&
        config.featureKycVerification
      ) {
        location = { name: "identityVerification" };
      } else if (
        to.name !== "parentalConsent" &&
        !isStaticPage(to) &&
        loggedIn &&
        allowAccess &&
        waitingForApproval &&
        isUnderAge &&
        !isAllowParentalAccess
      ) {
        location = { name: "parentalConsent" };
      } else if (loginPath(from) && !loginPath(to) && loggedIn && allowAccess && redirectPath) {
        location = redirectPath;
      }

      if (!loginPath(to) && location && !redirectPath) {
        store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, to);
      } else if (location === redirectPath) {
        store.commit(MUTATION_TYPES.SET_REDIRECT_PATH, null);
      }
      next(location);
    });
    router.afterEach(() => {
      HistoryManager.afterEachGuard.bind(HistoryManager);
      NProgress.done();
    });
  },
};
